import * as MuiIcons from "@mui/icons-material";
import * as Mui from "@mui/material";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import dayjs from "dayjs";
import { useEffect, useMemo, useState, useContext } from "react";
import { ContextOperation } from "../../operation/context/OperationContext";
import { useTranslation } from "react-i18next";
import ParkingToolSpace from "./ParkingToolTypes";
import { parkingToolActions } from "./parkingTool.actions";
import FileSaver from "file-saver";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { ContextParkingTool } from "./ParkingToolContext";
import CustomLoader from "../../../util/component/CustomLoader";

export const formatPositiveNumber = (num: number) => {
  const lookup = [
      { value: 1, symbol: "" },
      { value: 1e3, symbol: "k" },
      { value: 1e6, symbol: "M" },
  ];
  const rx = /\.0+$|(\.[0-9]*[1-9])0+$/;
  var item = lookup
      .slice()
      .reverse()
      .find(function (item) {
          return num >= item.value;
      });
  return item
      ? (num / item.value).toFixed(1).replace(rx, "$1") + item.symbol
      : "0";
};

export const formatNegativeNumber = (num: number) => {
  const lookup = [
      { value: -1, symbol: "" },
      { value: -1e3, symbol: "k" },
      { value: -1e6, symbol: "M" },
  ];
  const rx = /\.0+$|(\.[0-9]*[1-9])0+$/;
  var item = lookup
      .slice()
      .reverse()
      .find(function (item) {
          return num <= item.value;
      });
  return item
      ? (num / item.value).toFixed(1).replace(rx, "$1") + item.symbol
      : "0";
};

type Props = {
  singleLocationAvailability: ParkingToolSpace.AvailabilityParkingToolResponse;
  threshold: ParkingToolSpace.Threshhold[];
  getData: () => void;
}

const ParkingToolTable = (props: Props) => {
  const { t, i18n } = useTranslation(["network"]);

  const [rowData, setRowData] = useState<any[]>([]);
  const [columnDefs, setColumnDefs] = useState<ColDef[]>([]);
  const [updatedtime, setUpdatedtime] = useState(dayjs());
  const [loading, setLoading] = useState(false);
  const parkingToolAct = parkingToolActions();

  const formatTimeField = "HH:mm";
 
  const defaultColDef: ColDef = {
    suppressMovable: true,
  };
  
  const parkingToolLow = props.threshold?.filter((item) => item.thresholdName === "PARKING_TOOL_LOW")[0];
  const parkingToolMedium = props.threshold?.filter((item) => item.thresholdName === "PARKING_TOOL_MEDIUM")[0];
  const parkingToolHigh = props.threshold?.filter((item) => item.thresholdName === "PARKING_TOOL_HIGH")[0];
  
  const parkingToolContext = useContext(ContextParkingTool);

  
  const circleColor = (value: number) => {
    if (value < parkingToolLow?.maxValueExcluded) {
      return "error";
    }

    if (value >= parkingToolMedium?.minValueIncluded && value < parkingToolMedium?.maxValueExcluded) {
      return "warning";
    }

    if (value >= parkingToolHigh?.minValueIncluded) {
      return "success";
    }

    return "info";
  };
  
  const renderAvailabilityToolptip = (currentAvailability: number, currentAssignment: number, value: number) => {
    return (
      <div>
        {t("parkingTool.tooltipTitles.currentAvailability")}
        <span style={{ float: "right", marginLeft: "10px" }}>
          {currentAvailability ? "-" : ""}
          {currentAvailability}
        </span>
        <br />
        {t("parkingTool.tooltipTitles.currentAssignment")}
        <span style={{ float: "right", marginLeft: "10px" }}>
          {currentAssignment ? "-" : ""}
          {currentAssignment}
        </span>

        <br />

        <div
          style={{
            backgroundColor: "rgb(255,255,255, 0.5)",
            marginTop: "10px",
            marginLeft: "-16px",
            marginRight: "-16px",
            paddingLeft: "16px",
            paddingRight: "16px",
          }}
        >
          {t("parkingTool.tooltipTitles.total")}
          <span style={{ float: "right", marginLeft: "10px" }}>{value}</span>
        </div>
      </div>
    );
  };

  const handleToggleRows = (params: ICellRendererParams) => {
    const newRowData = rowData.map((row) => {
      if (
        row.acrissCategory === params.data.acrissCategory && !row.isAcriss
      ) {
        row.isVisible = !row.isVisible;
      }
      if (row.acrissCategory === params.data.acrissCategory && row.isAcriss) {
        row.childrenHidden = !row.childrenHidden;
      }
      return row;
    });

    setRowData(newRowData);
  };
  
  const renderAcrissLabel = (params: ICellRendererParams) => {
    return (
      <Mui.Tooltip arrow={true} title={params.data.acrissTooltip}>
        <Mui.Stack direction={"row"} justifyContent={!params.data.isAcriss || !params.data.hasChildren ? "end" : "space-between"} alignItems="center">
          {params.data.isAcriss && params.data.hasChildren && params.data.childrenHidden && (
            <Mui.IconButton onClick={() => handleToggleRows(params)}>
              <MuiIcons.Add />
            </Mui.IconButton>
          )}
          {params.data.isAcriss && params.data.hasChildren && !params.data.childrenHidden && (
            <Mui.IconButton onClick={() => handleToggleRows(params)}>
              <MuiIcons.Remove />
            </Mui.IconButton>
          )}

          <Mui.Typography>
            {params.value}
          </Mui.Typography>
        </Mui.Stack>
      </Mui.Tooltip>
    );
  };
  
  const timezoneOffset = useMemo(() => {
    return -1 * (new Date().getTimezoneOffset() / 60);
  }, []);

  const addColumn = (item: any): ColDef => {
    return {
      field: `${dayjs(item.previewDate).format(formatTimeField)}`,
      headerName: `${dayjs(item.previewDate).format(formatTimeField)}/${dayjs(item.previewDate).add(1, "hour").format(formatTimeField)}`,
      minWidth: 200,
      cellRenderer: (params: ICellRendererParams) => {
        const color = circleColor(params.value);

        if (!params.data[`${params.colDef?.field}-showValue`]) {
          return null;
        }

        return (
          <Mui.Tooltip
            arrow={true}
            title={renderAvailabilityToolptip(
              params.data[`${params.colDef?.field}-startReservationNeed`], 
              params.data[`${params.colDef?.field}-startUnavailabilityNeed`],
              params.value
            )}
          >
            <span
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "2px"
              }}
            >
              {isNaN(params.value)
                ? t("parkingTool.tooltipTitles.notAvailableAchronym")
                : params.value >= 0
                ? formatPositiveNumber(params.value)
                : `-${formatNegativeNumber(params.value)}`}

              <MuiIcons.Circle
                color={color}
                style={{
                  height: "10px",
                  width: "10px",
                }}
              />
            </span>
          </Mui.Tooltip>
        );
      },
    };
  };

  const elaborateColumnDefs = () => {
    setColumnDefs([
      {
        field: "acrissLabel",
        headerName: "",
        width: 150,
        pinned: "left",
        headerClass: "borderRight",
        cellRenderer: (params: ICellRendererParams) =>
          renderAcrissLabel(params),
      },
      {
        field: "current",
        headerName: t("parkingTool.current"),
        width: 300,
        headerComponent: () => {
          return (
            <Mui.Stack alignItems="center">
              <Mui.Grid container>
                <Mui.Grid item xs={4} justifyContent="center" display="flex">
                  {t("parkingTool.RRE")}
                </Mui.Grid>
                <Mui.Grid item xs={4} justifyContent="center" display="flex">
                  {t("parkingTool.TBC")}
                </Mui.Grid>
                <Mui.Grid item xs={4} justifyContent="center" display="flex">
                  {t("parkingTool.TOTAL")}
                </Mui.Grid>
              </Mui.Grid>
            </Mui.Stack>
          )
        },
        cellRenderer: (params: ICellRendererParams) => {
          return (
            <Mui.Grid container>
              <Mui.Grid item xs={4}>
                {params.data.currentRreAvailability}
              </Mui.Grid>

              <Mui.Grid item xs={4}>
                {params.data.currentTbcAvailability}
              </Mui.Grid>

              <Mui.Grid item xs={4}>
                {params.data.current}
              </Mui.Grid>
            </Mui.Grid>
          );
        },
      },
      ...props.singleLocationAvailability.singleLocationAcrissAvailabilityParkingToolResponses[0].aggregatedRows.map<ColDef>((element) => addColumn(element)),
    ])
  }

  const elaborateData = () => {
    let allRows: any[] = [];
    const dateValues: any = {};

    const rowsPerAcriss = props.singleLocationAvailability.singleLocationAcrissAvailabilityParkingToolResponses;

    rowsPerAcriss.map((rowPerAcriss) => {

      const realChildrenLength = rowPerAcriss.singleLocationAcrissSippAvailabilityParkingToolResponses.filter((item) => !!item.sipp).length;

      // acriss
      rowPerAcriss.aggregatedRows.forEach((el) => {
        const currentDate = `${dayjs(el.previewDate).format(formatTimeField)}`;
        dateValues[currentDate] = el.availability;
        dateValues[`${currentDate}-startReservationNeed`] = el.startReservationNeed;
        dateValues[`${currentDate}-startUnavailabilityNeed`] = el.startUnavailabilityNeed;
        dateValues[`${currentDate}-showValue`] = el.showValue;
      });

      allRows.push({
        acrissLabel: rowPerAcriss.acriss,
        acrissTooltip: rowPerAcriss.acrissLabel,
        acrissCategory: rowPerAcriss.acriss,
        current: rowPerAcriss.currentAvailability,
        currentRreAvailability: rowPerAcriss.currentRreAvailability,
        currentTbcAvailability: rowPerAcriss.currentTbcAvailability,
        isAcriss: true,
        isVisible: true,
        childrenHidden: true,
        hasChildren: realChildrenLength > 0 ? true : false,
        ...dateValues,
      });

      // singleSipps
      rowPerAcriss.singleLocationAcrissSippAvailabilityParkingToolResponses
        .filter((item) => !!item.sipp)
        .forEach((singleSipp) => {
          singleSipp.availabilityRows.forEach((el) => {
            const currentTime = `${dayjs(el.previewDate).format(formatTimeField)}`;
            dateValues[currentTime] = el.availability;
            dateValues[`${currentTime}-startReservationNeed`] = el.startReservationNeed;
            dateValues[`${currentTime}-startUnavailabilityNeed`] = el.startUnavailabilityNeed;
            dateValues[`${currentTime}-showValue`] = el.showValue;
          });
          allRows.push({
            acrissLabel: singleSipp.sipp,
            acrissTooltip: singleSipp.sippLabel,
            acrissCategory: rowPerAcriss.acriss,
            current: singleSipp.currentAvailability,
            currentRreAvailability: singleSipp.currentRreAvailability,
            currentTbcAvailability: singleSipp.currentTbcAvailability,
            isAcriss: false,
            isVisible: false,
            childrenHidden: true,
            ...dateValues,
          });
        });
      
      setRowData(allRows);
    });
  };
  
  useEffect(() => {
    elaborateColumnDefs();
  }, [rowData, i18n.language]);

  useEffect(() => {
    elaborateData();
  }, [props.singleLocationAvailability, props.threshold]);

  const onUpdate = () => {
    if (!props.getData) {
      return;
    }
    
    props.getData();
    setUpdatedtime(dayjs());
  }
  
  const handleDownloadExcel = () => {
    setLoading(true);

    console.log("parkingToolContext : " + JSON.stringify(parkingToolContext))

    if (parkingToolContext.tenant && parkingToolContext.request) {

      parkingToolAct.downloadParkingToolExcelReport(parkingToolContext.tenant, parkingToolContext.request)
        .then((response: any) => {
          setLoading(false);

          console.log(JSON.stringify(response));
          const filename = "parkingTool.xlsx";            
          var blob = new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          FileSaver.saveAs(blob, filename);
        });
      } else {
        setLoading(false);
      }
  };

  const disableDownloadExcel = () => {
    return !(parkingToolContext.tenant && parkingToolContext.request);
  }

  
  useEffect(() => {
    const intervalId = setInterval(onUpdate, 300000);

    return (() => {
      clearInterval(intervalId);
    });
  }, []);

  return (
    <Mui.Card style={{ height: "100%", width: "100%" }} className="ag-theme-alpine">
		<Mui.Box
              sx={{
                backgroundColor: "white",
                border: " 1px solid #dbe4e9",
                borderRadius: "2px",
                padding: "9px 1px 3px 2px",
              }}
            >

            <Mui.Grid
                container
                spacing={2}
                alignItems="center"
                direction="row"
                justifyContent="flex-end"
             >
              <Mui.Grid item>
                  <Mui.Button
                    variant="text"
                    size="icon"
                    onClick={handleDownloadExcel}
                    color="accent"
                    //disabled={disableDownloadExcel}
                  >
                    <Mui.Tooltip title={t("parkingTool.tooltipTitles.download")}>
                      <FileDownloadIcon />
                    </Mui.Tooltip>
                  </Mui.Button>
                </Mui.Grid>

            </Mui.Grid>
      </Mui.Box>

	
      <Mui.Card>
        <Mui.Stack direction="row" justifyContent="end">
          {`${t("parkingTool.updateTime")} ${updatedtime.format("DD/MM/YYYY HH:mm")}`}
        </Mui.Stack>
      </Mui.Card>
      <AgGridReact
        domLayout="autoHeight"
        rowHeight={42}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        rowData={rowData?.filter((row) => row.isVisible)}
        suppressRowHoverHighlight={true}
        tooltipShowDelay={0}
      />
       <CustomLoader loading={loading} />
    </Mui.Card>
  )
}

export default ParkingToolTable